import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Globe, CheckCircle, XCircle, Clock, Eye, Crown, Calendar, TicketPercent, Copy, Ban, Zap } from 'lucide-react';
import DiscountSettingsModal from '@/components/DiscountSettingsModal';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { openPreview } from '@/utils/preview';

type PlanTier = 'unpaid' | 'pro' | 'pro_max';
type StoredPlanTier = PlanTier | 'free' | 'paid';

const normalizePlanTier = (plan?: StoredPlanTier): PlanTier => {
    if (plan === 'pro_max' || plan === 'paid') return 'pro_max';
    if (plan === 'pro') return 'pro';
    return 'unpaid';
};

interface Domain {
    _id: string;
    slug: string;
    url: string;
    is_active: boolean;
    api_key: string;
    plan?: StoredPlanTier;
    planExpiresAt?: string | null;
    planPrice?: number;
    effectivePlan?: StoredPlanTier;
    isInGracePeriod?: boolean;
    gracePeriodDaysRemaining?: number;
    hasCompletedOnboarding?: boolean;
    approvalStatus?: 'pending' | 'approved' | 'rejected';
    approvalNote?: string;
    isPublished?: boolean;
}

const generateApiKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = '';
    for (let i = 0; i < 32; i++) {
        key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
};

const emptyDomain = {
    slug: '',
    url: '',
    is_active: true,
    api_key: '',
    plan: 'unpaid' as PlanTier,
    planPrice: 0,
    planExpiresAt: '',
};

interface PromoCodeRecord {
    _id: string;
    codeSuffix: string;
    assignedDomain: string;
    targetPlan: 'pro' | 'pro_max';
    durationDays: number;
    status: 'active' | 'redeeming' | 'redeemed' | 'revoked';
    redeemBy: string | null;
    redemptionDomain: string | null;
    redeemedAt: string | null;
    createdAt: string;
}

const DomainManager: React.FC = () => {
    const [domains, setDomains] = useState<Domain[]>([]);
    const [form, setForm] = useState(emptyDomain);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    const [planEditOpen, setPlanEditOpen] = useState(false);
    const [planEditId, setPlanEditId] = useState<string | null>(null);
    const [planForm, setPlanForm] = useState({
        plan: 'unpaid' as PlanTier,
        planExpiresAt: '',
    });
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [rejectSlug, setRejectSlug] = useState<string | null>(null);
    const [rejectNote, setRejectNote] = useState('');
    const [approvingSlug, setApprovingSlug] = useState<string | null>(null);
    const [discountOpen, setDiscountOpen] = useState(false);
    const [promoOpen, setPromoOpen] = useState(false);
    const [promoDomain, setPromoDomain] = useState<string | null>(null);
    const [promos, setPromos] = useState<PromoCodeRecord[]>([]);
    const [promoLoading, setPromoLoading] = useState(false);
    const [promoSaving, setPromoSaving] = useState(false);
    const [generatedPromoCode, setGeneratedPromoCode] = useState('');
    const [promoForm, setPromoForm] = useState({
        targetPlan: 'pro' as 'pro' | 'pro_max',
        durationDays: 365,
        redeemBy: '',
    });

    const fetchDomains = () => {
        setLoading(true);
        axios
            .get(`${BASE_URL}/api/domain`)
            .then((res) => setDomains(res.data))
            .finally(() => setLoading(false));
    };

    const fetchPromos = async (domainSlug: string) => {
        setPromoLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/api/promo-codes/admin`, { params: { domainSlug } });
            setPromos(response.data);
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to load promo codes');
        } finally {
            setPromoLoading(false);
        }
    };

    const openPromoManager = (domainSlug: string) => {
        setPromoDomain(domainSlug);
        setGeneratedPromoCode('');
        setPromoOpen(true);
        void fetchPromos(domainSlug);
    };

    const handleCreatePromo = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!promoDomain) return;
        setPromoSaving(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/promo-codes/admin`, {
                domainSlug: promoDomain,
                targetPlan: promoForm.targetPlan,
                durationDays: promoForm.durationDays,
                redeemBy: promoForm.redeemBy ? new Date(promoForm.redeemBy).toISOString() : null,
            });
            setGeneratedPromoCode(response.data.code);
            toast.success('Promo code created. Copy it now; it will not be shown again.');
            await fetchPromos(promoDomain);
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to create promo code');
        } finally {
            setPromoSaving(false);
        }
    };

    const handleCopyPromo = async () => {
        try {
            await navigator.clipboard.writeText(generatedPromoCode);
            toast.success('Promo code copied');
        } catch {
            toast.error('Copy failed. Select the code and copy it manually.');
        }
    };

    const handleRevokePromo = async (promoId: string) => {
        try {
            await axios.patch(`${BASE_URL}/api/promo-codes/admin/${promoId}/revoke`);
            setPromos((current) => current.map((promo) => (
                promo._id === promoId ? { ...promo, status: 'revoked' } : promo
            )));
            toast.success('Promo code revoked');
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to revoke promo code');
        }
    };

    useEffect(() => {
        fetchDomains();
    }, []);

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            const formData = {
                ...form,
                api_key: generateApiKey()
            };
            const response = await axios.post(`${BASE_URL}/api/domain`, formData);
            setAddOpen(false);
            setForm(emptyDomain);
            toast.success('Domain added successfully!');
            if (response.data) {
                setDomains(prev => [...prev, response.data]);
            } else {
                fetchDomains();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to add domain';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (item: Domain) => {
        setEditId(item._id);
        setForm({ 
            slug: item.slug, 
            url: item.url, 
            is_active: item.is_active,
            api_key: item.api_key,
            plan: normalizePlanTier(item.plan),
            planPrice: item.planPrice ?? 0,
            planExpiresAt: item.planExpiresAt ? new Date(item.planExpiresAt).toISOString().split('T')[0] : ''
        });
        setEditOpen(true);
        setError('');
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        const currentEditId = editId;
        try {
            const response = await axios.put(`${BASE_URL}/api/domain/${editId}`, form);
            setEditOpen(false);
            setEditId(null);
            setForm(emptyDomain);
            toast.success('Domain updated successfully!');
            if (response.data) {
                setDomains(prev => prev.map(domain =>
                    domain._id === currentEditId ? { ...domain, ...response.data } : domain
                ));
            } else {
                fetchDomains();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to update domain';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handlePlanEdit = (item: Domain) => {
        setPlanEditId(item._id);
        setPlanForm({
            plan: normalizePlanTier(item.plan),
            planExpiresAt: item.planExpiresAt ? new Date(item.planExpiresAt).toISOString().split('T')[0] : '',
        });
        setPlanEditOpen(true);
    };

    const handlePlanSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                plan: planForm.plan,
                planExpiresAt: planForm.plan !== 'unpaid' && planForm.planExpiresAt
                    ? new Date(planForm.planExpiresAt).toISOString() 
                    : null,
            };
            const response = await axios.patch(`${BASE_URL}/api/domain/${planEditId}/plan`, payload);
            setPlanEditOpen(false);
            setPlanEditId(null);
            toast.success('Plan updated successfully!');
            if (response.data) {
                setDomains(prev => prev.map(domain =>
                    domain._id === planEditId ? { ...domain, ...response.data } : domain
                ));
            } else {
                fetchDomains();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to update plan';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        const deletedDomain = domains.find(domain => domain._id === deleteId);
        // Optimistic removal
        setDomains(prev => prev.filter(domain => domain._id !== deleteId));
        setDeleteId(null);
        try {
            await axios.delete(`${BASE_URL}/api/domain/${deleteId}`);
            toast.success('Domain deleted successfully!');
        } catch (err: any) {
            // Revert on error
            if (deletedDomain) {
                setDomains(prev => [...prev, deletedDomain]);
            }
            const errorMessage = err.response?.data?.message || 'Failed to delete domain';
            toast.error(errorMessage);
        }
    };

    const handleApprove = async (slug: string) => {
        setApprovingSlug(slug);
        try {
            await axios.patch(`${BASE_URL}/api/domain/${slug}/approve`);
            setDomains(prev => prev.map(d => d.slug === slug ? { ...d, approvalStatus: 'approved', isPublished: true, approvalNote: '' } : d));
            toast.success(`Domain "${slug}" approved and published!`);
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to approve domain');
        } finally {
            setApprovingSlug(null);
        }
    };

    const handleReject = async () => {
        if (!rejectSlug) return;
        const slug = rejectSlug;
        try {
            await axios.patch(`${BASE_URL}/api/domain/${slug}/reject`, { note: rejectNote });
            setDomains(prev => prev.map(d => d.slug === slug ? { ...d, approvalStatus: 'rejected', isPublished: false, approvalNote: rejectNote } : d));
            toast.success(`Domain "${slug}" rejected.`);
            setRejectSlug(null);
            setRejectNote('');
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to reject domain');
        }
    };

    const handleToggleActive = async (id: string, currentStatus: boolean) => {
        setTogglingId(id);
        // Optimistic update
        setDomains(prev => prev.map(domain =>
            domain._id === id ? { ...domain, is_active: !currentStatus } : domain
        ));
        try {
            await axios.patch(
                `${BASE_URL}/api/domain/${id}/toggle-active`,
                {}
            );
            toast.success(`Domain ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
        } catch (err: any) {
            // Revert on error
            setDomains(prev => prev.map(domain =>
                domain._id === id ? { ...domain, is_active: currentStatus } : domain
            ));
            const errorMessage = err.response?.data?.message || 'Failed to update domain status';
            toast.error(errorMessage);
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className="page-title">Domain Management</h2>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setDiscountOpen(true)}>Discount Settings</Button>
                    <Dialog open={addOpen} onOpenChange={setAddOpen}>
                    <DialogTrigger asChild>
                        <Button>Add Domain</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Add New Domain</DialogTitle>
                            <DialogDescription>Create a new domain with slug, URL, and API key configuration</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAdd} className="space-y-5">
                            <Input
                                name="slug"
                                placeholder="Slug"
                                value={form.slug}
                                onChange={handleFormChange}
                                required
                            />
                            <Input
                                name="url"
                                placeholder="URL"
                                value={form.url}
                                onChange={handleFormChange}
                                required
                            />
                            <div className="flex items-center space-x-3">
                                <Switch
                                    checked={form.is_active}
                                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                                    aria-label="Toggle active state"
                                />
                                <span className={form.is_active ? 'status-label-active' : 'status-label-inactive'}>
                                    {form.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            {error && <div className="text-danger">{error}</div>}
                            <Button type="submit" loading={saving} loadingText="Adding...">Add</Button>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            <DiscountSettingsModal
                open={discountOpen}
                onOpenChange={setDiscountOpen}
                domains={domains}
            />

            <Dialog
                open={promoOpen}
                onOpenChange={(open) => {
                    setPromoOpen(open);
                    if (!open) setPromoDomain(null);
                }}
            >
                <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TicketPercent className="w-5 h-5 text-primary" />
                            Promo Codes · {promoDomain}
                        </DialogTitle>
                        <DialogDescription>Codes created here can only be redeemed by this domain.</DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreatePromo} className="grid gap-3 border-b border-border pb-5 sm:grid-cols-4 sm:items-end">
                        <div>
                            <label className="block text-sm font-medium mb-2">Plan</label>
                            <select
                                value={promoForm.targetPlan}
                                onChange={(event) => setPromoForm((current) => ({ ...current, targetPlan: event.target.value as 'pro' | 'pro_max' }))}
                                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                            >
                                <option value="pro">Pro</option>
                                <option value="pro_max">Pro Max</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Duration (days)</label>
                            <Input
                                type="number"
                                min={1}
                                max={3650}
                                value={promoForm.durationDays}
                                onChange={(event) => setPromoForm((current) => ({ ...current, durationDays: Number(event.target.value) }))}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Redeem by (optional)</label>
                            <Input
                                type="date"
                                min={new Date().toISOString().split('T')[0]}
                                value={promoForm.redeemBy}
                                onChange={(event) => setPromoForm((current) => ({ ...current, redeemBy: event.target.value }))}
                            />
                        </div>
                        <Button type="submit" loading={promoSaving} loadingText="Creating...">Create Code</Button>
                    </form>

                    {generatedPromoCode && (
                        <div className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center">
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-muted-foreground">Copy now. This code cannot be viewed again.</p>
                                <code className="mt-1 block select-all break-all text-base font-semibold">{generatedPromoCode}</code>
                            </div>
                            <Button type="button" variant="outline" onClick={handleCopyPromo}>
                                <Copy className="w-4 h-4" /> Copy
                            </Button>
                        </div>
                    )}

                    {promoLoading ? (
                        <PageLoader label="Loading promo codes..." />
                    ) : promos.length === 0 ? (
                        <p className="py-8 text-center text-sm text-muted-foreground">No promo codes created yet.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Code</TableHead>
                                    <TableHead>Plan</TableHead>
                                    <TableHead>Duration</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Redeemed by</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {promos.map((promo) => (
                                    <TableRow key={promo._id}>
                                        <TableCell className="font-mono">AI-••••••{promo.codeSuffix}</TableCell>
                                        <TableCell>{promo.targetPlan === 'pro_max' ? 'Pro Max' : 'Pro'}</TableCell>
                                        <TableCell>{promo.durationDays} days</TableCell>
                                        <TableCell className="capitalize">{promo.status}</TableCell>
                                        <TableCell>{promo.redemptionDomain || '—'}</TableCell>
                                        <TableCell className="text-right">
                                            {promo.status === 'active' && (
                                                <Button type="button" size="icon-sm" variant="ghost" onClick={() => handleRevokePromo(promo._id)} title="Revoke promo code">
                                                    <Ban className="w-4 h-4 text-danger" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>

            <div>
                {loading ? (
                    <PageLoader label="Loading domains..." />
                ) : domains.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <Globe className="w-full h-full" />
                        </div>
                        <h3 className="empty-state-title">No domains found</h3>
                        <p className="empty-state-description">Add your first domain to get started</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                <TableHead>Slug</TableHead>
                                <TableHead>URL</TableHead>
                                <TableHead>Approval</TableHead>
                                <TableHead>Plan</TableHead>
                                <TableHead>API Key</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-40">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {domains.map((item) => (
                                <TableRow 
                                    key={item._id} 
                                    className={`hover:bg-muted/50 transition-colors ${item.is_active ? 'bg-card' : 'bg-muted/50'}`}
                                >
                                    <TableCell className={`font-medium ${item.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        {item.slug}
                                    </TableCell>
                                    <TableCell className={`${item.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{item.url}</a>
                                    </TableCell>
                                    <TableCell>
                                        {!item.hasCompletedOnboarding ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-muted-foreground">
                                                Onboarding
                                            </span>
                                        ) : item.approvalStatus === 'approved' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                                <CheckCircle className="w-3 h-3" />
                                                Approved
                                            </span>
                                        ) : item.approvalStatus === 'rejected' ? (
                                            <div>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                                                        <XCircle className="w-3 h-3" />
                                                        Rejected
                                                    </span>
                                                    <Button
                                                        size="icon-sm"
                                                        variant="outline"
                                                        className="h-6 w-6"
                                                        onClick={() => openPreview(item.slug).catch(() => toast.error('Failed to open preview'))}
                                                        title="Preview website"
                                                    >
                                                        <Eye className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                                {item.approvalNote && (
                                                    <p className="text-xs text-muted-foreground mt-1 max-w-[160px] truncate" title={item.approvalNote}>{item.approvalNote}</p>
                                                )}
                                            </div>
                                        ) : item.approvalStatus === 'pending' ? (
                                            <div className="flex items-center gap-1.5">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                                    <Clock className="w-3 h-3" />
                                                    Pending
                                                </span>
                                                <Button
                                                    size="icon-sm"
                                                    variant="outline"
                                                    className="h-6 w-6"
                                                    onClick={() => openPreview(item.slug).catch(() => toast.error('Failed to open preview'))}
                                                    title="Preview website"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button
                                                    size="icon-sm"
                                                    variant="outline"
                                                    className="h-6 w-6 text-green-600 hover:bg-green-50 hover:text-green-700"
                                                    onClick={() => handleApprove(item.slug)}
                                                    disabled={approvingSlug === item.slug}
                                                    title="Approve"
                                                >
                                                    <CheckCircle className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button
                                                    size="icon-sm"
                                                    variant="outline"
                                                    className="h-6 w-6 text-red-600 hover:bg-red-50 hover:text-red-700"
                                                    onClick={() => { setRejectSlug(item.slug); setRejectNote(''); }}
                                                    title="Reject"
                                                >
                                                    <XCircle className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const tier = normalizePlanTier(item.effectivePlan || item.plan);
                                            const isPaid = tier !== 'unpaid';
                                            return (
                                        <>
                                        <button
                                            onClick={() => handlePlanEdit(item)}
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer hover:opacity-80 ${
                                                isPaid
                                                    ? 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200'
                                                    : 'bg-secondary text-muted-foreground'
                                            }`}
                                        >
                                            {isPaid ? (
                                                <>
                                                    <Crown className="w-3 h-3" />
                                                    {tier === 'pro_max' ? 'Pro Max' : 'Pro'}
                                                    {item.isInGracePeriod && (
                                                        <span className="text-red-600 ml-1">({item.gracePeriodDaysRemaining}d left)</span>
                                                    )}
                                                </>
                                            ) : (
                                                'Unpaid'
                                            )}
                                        </button>
                                        {item.planExpiresAt && isPaid && !item.isInGracePeriod && (
                                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                Expires: {new Date(item.planExpiresAt).toLocaleDateString()}
                                            </div>
                                        )}
                                        </>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <code className={`px-2 py-1 rounded text-xs break-all ${item.is_active ? 'bg-secondary text-foreground' : 'bg-secondary text-muted-foreground'}`}>
                                            {item.api_key}
                                        </code>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={item.is_active}
                                                onCheckedChange={() => handleToggleActive(item._id, item.is_active)}
                                                disabled={togglingId === item._id}
                                            />
                                            <span className={item.is_active ? 'status-label-active' : 'status-label-inactive'}>
                                                {item.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="icon-sm"
                                            onClick={() => openPromoManager(item.slug)}
                                            title={`Manage promo codes for ${item.slug}`}
                                        >
                                            <TicketPercent className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon-sm"
                                            onClick={() => handlePlanEdit(item)}
                                            title="Edit Plan"
                                        >
                                            <Crown className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon-sm"
                                            onClick={() => handleEdit(item)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            size="icon-sm"
                                            onClick={() => setDeleteId(item._id)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                            </svg>
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Domain</DialogTitle>
                        <DialogDescription>Update domain information including slug, URL, and active status</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit} className="space-y-5">
                        <Input
                            name="slug"
                            placeholder="Slug"
                            value={form.slug}
                            onChange={handleFormChange}
                            required
                        />
                        <Input
                            name="url"
                            placeholder="URL"
                            value={form.url}
                            onChange={handleFormChange}
                            required
                        />
                        <div className="flex items-center space-x-3">
                            <Switch
                                checked={form.is_active}
                                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                                aria-label="Toggle active state"
                            />
                            <span className={form.is_active ? 'status-label-active' : 'status-label-inactive'}>
                                {form.is_active ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                        {error && <div className="text-danger">{error}</div>}
                        <Button type="submit" loading={saving} loadingText="Saving...">Save</Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={!!deleteId}
                onOpenChange={(open) => !open && setDeleteId(null)}
                title="Delete Domain"
                description="Are you sure you want to delete this domain? This action cannot be undone."
                confirmLabel="Delete"
                onConfirm={handleDelete}
            />

            {/* Plan Edit Dialog */}
            <Dialog open={planEditOpen} onOpenChange={setPlanEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Crown className="w-5 h-5 text-primary" />
                            Manage Subscription Plan
                        </DialogTitle>
                        <DialogDescription>
                            Update the subscription plan for this domain. Changes take effect immediately.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handlePlanSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium mb-2">Plan Type</label>
                            <div className="grid grid-cols-3 gap-3">
                                {([
                                    { value: 'unpaid', label: 'Unpaid', detail: 'Private only', icon: Globe },
                                    { value: 'pro', label: 'Pro', detail: '₹99 / month', icon: Zap },
                                    { value: 'pro_max', label: 'Pro Max', detail: '₹599 / month', icon: Crown },
                                ] as const).map(({ value, label, detail, icon: Icon }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setPlanForm(prev => ({ ...prev, plan: value }))}
                                        className={`p-3 rounded-lg border-2 transition-all ${
                                            planForm.plan === value
                                                ? 'border-primary bg-primary/5'
                                                : 'border-border hover:border-muted-foreground/50'
                                        }`}
                                    >
                                        <div className="font-semibold flex items-center justify-center gap-1">
                                            <Icon className="w-4 h-4 text-primary" />
                                            {label}
                                        </div>
                                        <div className="text-xs text-muted-foreground">{detail}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Catalog price</span>
                                <span className="font-semibold">
                                    {planForm.plan === 'unpaid' && 'No public entitlement'}
                                    {planForm.plan === 'pro' && '₹99/month · ₹999/year'}
                                    {planForm.plan === 'pro_max' && '₹599/month · ₹4,999/year'}
                                </span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">Prices are controlled by the server catalog and cannot be overridden here.</p>
                        </div>
                        
                        {planForm.plan !== 'unpaid' && (
                            <div>
                                <label className="block text-sm font-medium mb-2">Plan Expiry Date</label>
                                <Input
                                    type="date"
                                    value={planForm.planExpiresAt}
                                    onChange={(e) => setPlanForm(prev => ({ ...prev, planExpiresAt: e.target.value }))}
                                    min={new Date().toISOString().split('T')[0]}
                                />
                                <p className="text-xs text-muted-foreground mt-1">Leave empty for no expiry. 7-day grace period applies after expiry.</p>
                            </div>
                        )}
                        
                        <div className="flex gap-2 pt-2">
                            <Button type="submit" loading={saving} loadingText="Saving..." className="flex-1">
                                Save Plan
                            </Button>
                            <Button type="button" variant="outline" onClick={() => setPlanEditOpen(false)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reject Confirmation Dialog */}
            <Dialog open={!!rejectSlug} onOpenChange={(open) => { if (!open) { setRejectSlug(null); setRejectNote(''); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <XCircle className="w-5 h-5 text-red-500" />
                            Reject Domain
                        </DialogTitle>
                        <DialogDescription>
                            Rejecting <strong>{rejectSlug}</strong> will unpublish the website. The owner will be notified.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Reason (optional)</label>
                            <Textarea
                                value={rejectNote}
                                onChange={(e) => setRejectNote(e.target.value)}
                                placeholder="e.g. Content incomplete, placeholder images, etc."
                                rows={3}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button variant="destructive" onClick={handleReject}>
                                Reject
                            </Button>
                            <Button variant="outline" onClick={() => { setRejectSlug(null); setRejectNote(''); }}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DomainManager;
